/**
 * Flange Scope Context
 *
 * Mirrors EEHA's client scoping (EEHAClientContext) for the Flange Management
 * module: a persistent Client → Site → Plant selection shared across every
 * flange page, backed by sessionStorage so it sticks as the user navigates.
 *
 * Differences from EEHA: flange permissions come from the mechanical role
 * family — a single global access level (mechanical_flange:readonly/default/
 * admin) plus a client scope (mechanical_clients:*). So there's one
 * accessLevel for the whole module rather than a per-client permission.
 *
 * Endpoints (backend/routes/flangeConfig.ts):
 *   GET /api/flange-config/clients        → [{ client, sites:[{name,plants}] }]
 *   GET /api/flange-config/my-permissions → { isAdmin, accessLevel, allClients,
 *                                             clients: 'all'|string[], ... }
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import axios from 'axios';

export type FlangeAccessLevel = 'readonly' | 'edit' | 'admin';

export interface FlangeClientInfo {
  name: string;
  sites: Array<{ name: string; plants?: string[] }>;
}

interface FlangePermissions {
  isAdmin: boolean;
  accessLevel: FlangeAccessLevel;
  allClients: boolean;
  clients: 'all' | string[];
}

export interface FlangeScopeContextValue {
  isAdmin: boolean;
  accessLevel: FlangeAccessLevel;
  isLoading: boolean;
  error: string | null;

  clients: FlangeClientInfo[];

  selectedClient: string | null;
  selectedSite: string | null;
  selectedPlant: string | null;
  setSelectedClient: (client: string | null) => void;
  setSelectedSite: (site: string | null) => void;
  setSelectedPlant: (plant: string | null) => void;

  availableSites: string[];
  availablePlants: string[];

  canEdit: boolean;
  canConfigure: boolean;

  refresh: () => Promise<void>;
}

const FlangeScopeContext = createContext<FlangeScopeContextValue | null>(null);

const STORAGE_KEY_CLIENT = 'flange_selected_client';
const STORAGE_KEY_SITE = 'flange_selected_site';
const STORAGE_KEY_PLANT = 'flange_selected_plant';

function readStored(key: string): string | null {
  try {
    const v = sessionStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

interface FlangeScopeProviderProps {
  children: ReactNode;
}

export function FlangeScopeProvider({ children }: FlangeScopeProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<FlangePermissions | null>(null);
  const [hierarchy, setHierarchy] = useState<
    Array<{ client: string; sites: Array<{ name: string; plants?: string[] }> }>
  >([]);

  const [selectedClient, setSelectedClientState] = useState<string | null>(() => readStored(STORAGE_KEY_CLIENT));
  const [selectedSite, setSelectedSiteState] = useState<string | null>(() => readStored(STORAGE_KEY_SITE));
  const [selectedPlant, setSelectedPlantState] = useState<string | null>(() => readStored(STORAGE_KEY_PLANT));

  // Selecting a client clears the downstream site/plant.
  const setSelectedClient = useCallback((client: string | null) => {
    setSelectedClientState(client);
    setSelectedSiteState(null);
    setSelectedPlantState(null);
    try {
      if (client === null) sessionStorage.removeItem(STORAGE_KEY_CLIENT);
      else sessionStorage.setItem(STORAGE_KEY_CLIENT, JSON.stringify(client));
      sessionStorage.removeItem(STORAGE_KEY_SITE);
      sessionStorage.removeItem(STORAGE_KEY_PLANT);
    } catch (err) {
      console.error('[FlangeScope] persist client failed:', err);
    }
  }, []);

  const setSelectedSite = useCallback((site: string | null) => {
    setSelectedSiteState(site);
    setSelectedPlantState(null);
    try {
      if (site === null) sessionStorage.removeItem(STORAGE_KEY_SITE);
      else sessionStorage.setItem(STORAGE_KEY_SITE, JSON.stringify(site));
      sessionStorage.removeItem(STORAGE_KEY_PLANT);
    } catch (err) {
      console.error('[FlangeScope] persist site failed:', err);
    }
  }, []);

  const setSelectedPlant = useCallback((plant: string | null) => {
    setSelectedPlantState(plant);
    try {
      if (plant === null) sessionStorage.removeItem(STORAGE_KEY_PLANT);
      else sessionStorage.setItem(STORAGE_KEY_PLANT, JSON.stringify(plant));
    } catch (err) {
      console.error('[FlangeScope] persist plant failed:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [permsRes, hierRes] = await Promise.all([
        axios.get('/api/flange-config/my-permissions'),
        axios.get('/api/flange-config/clients'),
      ]);
      const perms: FlangePermissions = permsRes.data?.data || permsRes.data;
      const hier = (hierRes.data?.data || hierRes.data || []) as Array<{
        client: string;
        sites: Array<{ name: string; plants?: string[] }>;
      }>;
      setPermissions(perms);
      setHierarchy(hier);

      // Keep a valid stored client; otherwise auto-select when there's only one.
      const accessible = perms.allClients
        ? hier.map(h => h.client)
        : (Array.isArray(perms.clients) ? perms.clients : []);
      const stored = readStored(STORAGE_KEY_CLIENT);
      const storedValid = stored && accessible.some(c => c.toLowerCase() === stored.toLowerCase());
      if (!storedValid) {
        if (accessible.length === 1) setSelectedClient(accessible[0]);
        else if (stored) setSelectedClient(null); // stored no longer permitted
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load flange scope';
      console.error('[FlangeScope] load error:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [setSelectedClient]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clients = useMemo<FlangeClientInfo[]>(() => {
    if (!permissions) return [];
    const allowAll = permissions.allClients;
    const permitted = Array.isArray(permissions.clients) ? permissions.clients.map(c => c.toLowerCase()) : [];
    return hierarchy
      .filter(h => allowAll || permitted.includes(h.client.toLowerCase()))
      .map(h => ({ name: h.client, sites: h.sites || [] }));
  }, [permissions, hierarchy]);

  const availableSites = useMemo<string[]>(() => {
    if (!selectedClient) return [];
    const c = hierarchy.find(h => h.client.toLowerCase() === selectedClient.toLowerCase());
    return c?.sites.map(s => s.name) || [];
  }, [selectedClient, hierarchy]);

  const availablePlants = useMemo<string[]>(() => {
    if (!selectedClient || !selectedSite) return [];
    const c = hierarchy.find(h => h.client.toLowerCase() === selectedClient.toLowerCase());
    const s = c?.sites.find(x => x.name.toLowerCase() === selectedSite.toLowerCase());
    return s?.plants || [];
  }, [selectedClient, selectedSite, hierarchy]);

  const accessLevel = permissions?.accessLevel || 'readonly';

  const value = useMemo<FlangeScopeContextValue>(
    () => ({
      isAdmin: permissions?.isAdmin || false,
      accessLevel,
      isLoading,
      error,
      clients,
      selectedClient,
      selectedSite,
      selectedPlant,
      setSelectedClient,
      setSelectedSite,
      setSelectedPlant,
      availableSites,
      availablePlants,
      canEdit: accessLevel === 'edit' || accessLevel === 'admin',
      canConfigure: accessLevel === 'admin',
      refresh: loadData,
    }),
    [
      permissions?.isAdmin,
      accessLevel,
      isLoading,
      error,
      clients,
      selectedClient,
      selectedSite,
      selectedPlant,
      setSelectedClient,
      setSelectedSite,
      setSelectedPlant,
      availableSites,
      availablePlants,
      loadData,
    ],
  );

  return <FlangeScopeContext.Provider value={value}>{children}</FlangeScopeContext.Provider>;
}

export function useFlangeScope(): FlangeScopeContextValue {
  const ctx = useContext(FlangeScopeContext);
  if (!ctx) throw new Error('useFlangeScope must be used within a FlangeScopeProvider');
  return ctx;
}

export default FlangeScopeContext;
